// ===========================================
// Trusted Devices Dialog Component
// ===========================================
// List and revoke trusted devices for 2FA

import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DevicesOther as DeviceIcon,
  Delete as DeleteIcon,
  PhoneAndroid as PhoneIcon,
  Computer as ComputerIcon,
  DeleteSweep as DeleteSweepIcon,
} from '@mui/icons-material';
import { useTrustedDevices, useRevokeDevice, useRevokeAllDevices } from '../../../hooks/useTwoFactor';
import type { TrustedDevice } from '../../../api/twoFactor.api';

interface Props {
  open: boolean;
  onClose: () => void;
}

function getDeviceIcon(userAgent: string | null) {
  if (!userAgent) return <DeviceIcon />;
  const ua = userAgent.toLowerCase();
  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
    return <PhoneIcon />;
  }
  return <ComputerIcon />;
}

function getDeviceDisplayName(device: TrustedDevice): string {
  if (device.deviceName) return device.deviceName;
  if (!device.userAgent) return 'Unknown Device';
  // Extract a reasonable name from user agent
  const ua = device.userAgent;
  if (ua.includes('Chrome')) return 'Chrome Browser';
  if (ua.includes('Firefox')) return 'Firefox Browser';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari Browser';
  if (ua.includes('Edge')) return 'Edge Browser';
  return 'Unknown Browser';
}

export default function TrustedDevicesDialog({ open, onClose }: Props) {
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);

  const { data: devicesData, isLoading } = useTrustedDevices();
  const revokeDevice = useRevokeDevice();
  const revokeAllDevices = useRevokeAllDevices();

  const devices = devicesData?.data?.devices || [];

  const handleRevokeAll = async () => {
    await revokeAllDevices.mutateAsync();
    setConfirmRevokeAll(false);
  };

  const handleClose = () => {
    setConfirmRevokeAll(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Trusted Devices</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : devices.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <DeviceIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No trusted devices
            </Typography>
            <Typography variant="body2" color="text.secondary">
              When you sign in with 2FA and choose "Trust this device", it will appear here.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Trusted devices can sign in without entering a 2FA code.
            </Typography>
            <List disablePadding>
              {devices.map((device, index) => (
                <Box key={device.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    secondaryAction={
                      !device.isCurrent ? (
                        <Tooltip title="Revoke">
                          <IconButton
                            edge="end"
                            onClick={() => revokeDevice.mutateAsync(device.id)}
                            disabled={revokeDevice.isPending}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      ) : undefined
                    }
                  >
                    <ListItemIcon>
                      {getDeviceIcon(device.userAgent)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getDeviceDisplayName(device)}
                          {device.isCurrent && (
                            <Chip label="Current" size="small" color="primary" variant="outlined" />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          {device.ipAddress && `IP: ${device.ipAddress}`}
                          {device.lastUsedAt && ` · Last used: ${new Date(device.lastUsedAt).toLocaleDateString()}`}
                          {` · Expires: ${new Date(device.trustedUntil).toLocaleDateString()}`}
                        </>
                      }
                    />
                  </ListItem>
                </Box>
              ))}
            </List>

            {devices.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                {confirmRevokeAll ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="error">
                      Revoke all devices? You'll need to re-verify on each device.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" onClick={() => setConfirmRevokeAll(false)}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="contained"
                        onClick={handleRevokeAll}
                        disabled={revokeAllDevices.isPending}
                      >
                        {revokeAllDevices.isPending ? 'Revoking...' : 'Confirm'}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Button
                    color="error"
                    size="small"
                    startIcon={<DeleteSweepIcon />}
                    onClick={() => setConfirmRevokeAll(true)}
                  >
                    Revoke All Devices
                  </Button>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
